import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Guard for routes that require an authenticated session.
 *
 * Behaviour:
 *  - While AuthContext is still resolving /auth/me (after a refresh / cold start),
 *    show a centered spinner so we don't briefly flash the login page for users
 *    that ARE logged in (no redirect loop on slow networks).
 *  - When resolved and unauthenticated, redirect to "/" while remembering the
 *    page that triggered the redirect (so we could optionally bounce back after
 *    login — kept as a `from` in router state).
 *  - When resolved and authenticated, optionally enforce `adminOnly`.
 */
export default function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }
  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/home" replace />;
  }
  return children;
}
